import { useNavigate } from "react-router-dom";
import { ScanFace, ArrowLeft } from "lucide-react";

const UPDATED = "Última atualização: junho de 2026";

function LegalShell({ title, children }) {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-cream text-charcoal-900">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-cream/80 border-b border-line">
        <div className="max-w-3xl mx-auto px-6 h-16 flex items-center justify-between">
          <button onClick={() => navigate("/")} data-testid="legal-home" className="flex items-center gap-2 text-charcoal-700 hover:text-accent transition-colors">
            <ArrowLeft className="w-4 h-4" /> Início
          </button>
          <div className="flex items-center gap-2 font-serif text-lg"><ScanFace className="w-4 h-4 text-accent" /> VisagismoIA</div>
        </div>
      </header>
      <main className="max-w-3xl mx-auto px-6 py-14">
        <span className="text-xs tracking-[0.25em] uppercase text-accent font-semibold">Documento legal</span>
        <h1 className="font-serif text-4xl md:text-5xl mt-3">{title}</h1>
        <p className="text-charcoal-500 mt-2 text-sm">{UPDATED}</p>
        <div className="mt-8 space-y-6 text-charcoal-700 leading-relaxed">{children}</div>
        <p className="mt-12 text-sm text-charcoal-500">Em caso de dúvidas, entre em contato pelo e-mail de suporte informado no site.</p>
      </main>
    </div>
  );
}

const H = ({ children }) => <h2 className="font-serif text-2xl text-charcoal-900 mt-8">{children}</h2>;
const P = ({ children }) => <p>{children}</p>;
const LI = ({ children }) => <li className="ml-5 list-disc">{children}</li>;

export function Privacidade() {
  return (
    <LegalShell title="Política de Privacidade">
      <P>Esta Política de Privacidade descreve como o Visagismo IA ("nós", "nosso") coleta, utiliza, armazena e protege as informações dos usuários ("você") ao utilizar nosso serviço de análise de visagismo por inteligência artificial.</P>

      <H>1. Informações que coletamos</H>
      <ul className="space-y-2">
        <LI><strong>Fotos enviadas:</strong> as fotos de frente e de perfil que você envia para a geração do seu estudo de visagismo.</LI>
        <LI><strong>Dados do pedido:</strong> gênero selecionado, data do pedido e status do pagamento.</LI>
        <LI><strong>Dados de pagamento:</strong> o pagamento é processado de forma segura pela Kiwify. Não temos acesso aos dados do seu cartão.</LI>
      </ul>

      <H>2. Como usamos suas informações</H>
      <P>Utilizamos as suas fotos exclusivamente para gerar a sua análise de visagismo e as imagens de resultado. Os demais dados são usados para processar o pagamento, entregar o seu estudo em PDF e prestar suporte.</P>

      <H>3. Inteligência Artificial</H>
      <P>Suas fotos são processadas por modelos de inteligência artificial para análise facial e geração de imagens de resultado. Esse processamento tem como única finalidade a criação do seu estudo personalizado.</P>

      <H>4. Compartilhamento de dados</H>
      <P>Não vendemos nem alugamos os seus dados pessoais. Compartilhamos informações apenas com prestadores necessários à operação do serviço (por exemplo, o processador de pagamentos Kiwify e os provedores de IA), sempre limitados à finalidade descrita.</P>

      <H>5. Armazenamento e segurança</H>
      <P>Adotamos medidas técnicas e organizacionais para proteger as suas informações. Você pode solicitar a exclusão das suas fotos e dados a qualquer momento pelo nosso canal de contato.</P>

      <H>6. Seus direitos (LGPD)</H>
      <P>Em conformidade com a Lei Geral de Proteção de Dados (Lei nº 13.709/2018), você tem o direito de acessar, corrigir, portar e solicitar a exclusão dos seus dados, bem como revogar o consentimento.</P>

      <H>7. Alterações</H>
      <P>Podemos atualizar esta Política periodicamente. A data da última atualização é sempre indicada no topo desta página.</P>
    </LegalShell>
  );
}

export function Termos() {
  return (
    <LegalShell title="Termos de Uso">
      <P>Ao utilizar o Visagismo IA, você concorda com os termos e condições descritos abaixo. Leia com atenção antes de realizar sua compra.</P>

      <H>1. O serviço</H>
      <P>O Visagismo IA oferece uma análise de visagismo gerada por inteligência artificial, entregue em formato de PDF, contendo recomendações de cortes, estilo e imagens de resultado. O serviço tem caráter informativo e estético.</P>

      <H>2. Pagamento</H>
      <P>O valor do estudo é de R$67 (pagamento único), processado com segurança via Kiwify. O estudo em PDF só é liberado após a confirmação do pagamento.</P>

      <H>3. Uso adequado</H>
      <ul className="space-y-2">
        <LI>Você declara ser maior de 18 anos ou possuir autorização de um responsável.</LI>
        <LI>Você se compromete a enviar apenas fotos suas ou para as quais possua autorização.</LI>
        <LI>É proibido enviar conteúdo ilegal, ofensivo ou que viole direitos de terceiros.</LI>
      </ul>

      <H>4. Natureza das recomendações</H>
      <P>As recomendações e imagens são sugestões geradas por IA e podem variar conforme a interpretação profissional. Não garantimos resultados idênticos, uma vez que a execução depende do profissional escolhido (barbeiro ou cabeleireiro) e das características individuais.</P>

      <H>5. Reembolso</H>
      <P>Por se tratar de um produto digital personalizado e gerado sob demanda, o reembolso será avaliado caso a caso. Em caso de falha técnica que impeça a entrega do estudo, entre em contato para regularização ou reembolso.</P>

      <H>6. Propriedade intelectual</H>
      <P>O conteúdo do estudo é para uso pessoal do cliente. A marca, o layout e os textos do site são de propriedade do Visagismo IA.</P>

      <H>7. Limitação de responsabilidade</H>
      <P>O serviço é fornecido "como está". Não nos responsabilizamos por decisões estéticas tomadas com base nas recomendações nem por resultados executados por terceiros.</P>

      <H>8. Alterações dos termos</H>
      <P>Podemos alterar estes Termos a qualquer momento. O uso continuado do serviço após alterações implica concordância com a versão vigente.</P>
    </LegalShell>
  );
}
